import React, { Fragment, useState, useEffect } from "react";
import {
  AssistantRounded,
  SchemaRounded,
  SnippetFolderRounded,
  DatasetRounded,
  DataExplorationRounded,
  ExpandMoreRounded,
  AssistantOutlined,
  SchemaOutlined,
  DataExplorationOutlined,
  DatasetOutlined,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  PID_URL,
  AI_SEARCH_URL,
  DATA_EXPLORER_URL,
  DATA_MANAGEMENT_URL,
} from "../../../utils/routes";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { GradientIconLight } from "../../../components/GradientIcon";
import { useDispatch, useSelector } from "react-redux";
import { getSessionMessagesSuccess } from "../../../core/events/sessionEvents";
import { getAllSessions } from "../../../core/repo/sessionRepo";
import RecentSessions from "./RecentSessions";
import { useThemeSettings } from "../../../theme/ThemeSettingsProvider";

const useStyles = makeStyles()((theme, { expanded }, classes) => ({
  activeNavItem: {
    background: expanded ? theme.palette.primary.lightBg : "none",
    [`& .${classes.labelText}`]: {
      fontWeight: 500,
      background: theme.palette.light,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    [`& .${classes.listItemIcon}`]: {
      background: !expanded ? theme.palette.primary.lightBg : "none",
    },
  },
  comingSoonLabel: {
    fontSize: 10,
    fontWeight: 400,
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textAlign: "center",
    marginTop: 2,
  },
  collapsedMenu: {
    flexDirection: "column",
    paddingLeft: 0,
    paddingRight: 0,
    [`& .${classes.listItemIcon}`]: {
      minWidth: 24,
      padding: 4,
      borderRadius: 6,
    },
    [`& .${classes.label}`]: {
      fontSize: 11,
      width: 48,
      textOverflow: "ellipsis",
      textAlign: "center",
    },
  },
  label: {
    fontSize: 14,
  },
  labelText: {
    fontWeight: 500,
    background: theme.palette.light,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  listItemIcon: {},
}));

const topNavItems = [
  {
    label: "Search",
    roundedIcon: AssistantRounded,
    outlinedIcon: AssistantOutlined,
    path: AI_SEARCH_URL,
  },
  {
    label: "P&ID",
    roundedIcon: SchemaRounded,
    outlinedIcon: SchemaOutlined,
    path: PID_URL,
    // disabled: true,
  },
];

const dataNavItems = [
  {
    label: "Data Explorer",
    roundedIcon: DataExplorationRounded,
    outlinedIcon: DataExplorationOutlined,
    path: DATA_EXPLORER_URL,
  },
  {
    label: "Data Management",
    roundedIcon: DatasetRounded,
    outlinedIcon: DatasetOutlined,
    path: DATA_MANAGEMENT_URL,
  },
];

function NavItems({ expanded }) {
  const dispatch = useDispatch();
  const { sessions } = useSelector((state) => state.session);
  const { selectedDomain } = useSelector((state) => state.domain);
  const [recentSessions, setRecentSessions] = useState([]);
  const { mode } = useThemeSettings();
  const isDarkMode = mode === "dark";

  useEffect(() => {
    if (selectedDomain) {
      fetchSessions();
    }
  }, [selectedDomain]);

  useEffect(() => {
    // Get the last 3 sessions
    if (sessions && sessions.length > 0) {
      setRecentSessions(sessions.slice(0, 3));
    } else {
      setRecentSessions([]);
    }
  }, [sessions]);

  const fetchSessions = async () => {
    try {
      await dispatch(getAllSessions(selectedDomain.id));
    } catch (err) {
      console.log("Error fetching sessions:", err);
    }
  };

  return (
    <Box display={"flex"} flexDirection={"column"} flex={1} overflow={"hidden"}>
      <Box flex={1} overflow={"scroll"}>
        {expanded && (
          <Box px={1.5} mb={0.5} mt={2}>
            <Typography
              variant="caption"
              color={"textSecondary"}
              sx={{ letterSpacing: "1.5px", fontWeight: 500 }}>
              AI AGENTS
            </Typography>
          </Box>
        )}
        <Box mt={!expanded ? 2 : 0}>
          <NavItemsList
            isDarkMode={isDarkMode}
            items={topNavItems}
            expanded={expanded}
            recentSessions={recentSessions}
            totalSessionsCount={sessions.length}
          />
        </Box>
      </Box>
      {expanded && <Divider sx={{ my: 1 }} />}
      <DataNavItems expanded={expanded} isDarkMode={isDarkMode} />
      {expanded && <Divider sx={{ my: 1 }} />}
    </Box>
  );
}

function DataNavItems({ expanded, isDarkMode }) {
  return expanded ? (
    <Box>
      <Box px={1.5} mb={0.5}>
        <Typography
          variant="caption"
          color={"textSecondary"}
          sx={{ letterSpacing: "2px", fontWeight: 500 }}>
          DATA
        </Typography>
      </Box>
      <NavItemsList
        isDarkMode={isDarkMode}
        items={dataNavItems}
        expanded={true}
        totalSessionsCount={0}
      />
    </Box>
  ) : (
    <DataNavItemsWithMenu isDarkMode={isDarkMode} />
  );
}

function DataNavItemsWithMenu({ isDarkMode }) {
  const theme = useTheme();
  const { classes } = useStyles({ expanded: false });
  const history = useHistory();
  const location = useLocation();
  const isSelected =
    location.pathname.startsWith(DATA_EXPLORER_URL) ||
    location.pathname.startsWith(DATA_MANAGEMENT_URL);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickNavItem = (navItem) => {
    const { path } = navItem;
    history.push({
      pathname: path,
    });
    handleClose();
  };
  return (
    <Fragment>
      <Box px={1.5}>
        <Tooltip title={"Data"} arrow placement="right">
          <MenuItem
            className={`${isSelected ? classes.activeNavItem : null} ${
              classes.collapsedMenu
            }`}
            sx={{ padding: 1, borderRadius: 1 }}
            onClick={handleClick}>
            <ListItemIcon classes={{ root: classes.listItemIcon }}>
              {isSelected ? (
                isDarkMode ? (
                  <SnippetFolderRounded
                    sx={{ color: theme.palette.text.primary }}
                  />
                ) : (
                  <GradientIconLight Icon={SnippetFolderRounded} />
                )
              ) : (
                <SnippetFolderRounded />
              )}
            </ListItemIcon>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}>
              <Typography sx={{ fontSize: 11 }} color="textSecondary">
                <span className={isSelected ? classes.labelText : ""}>
                  Data
                </span>
              </Typography>
              {isSelected ? (
                isDarkMode ? (
                  <ExpandMoreRounded
                    sx={{ color: theme.palette.text.primary }}
                  />
                ) : (
                  <GradientIconLight
                    Icon={ExpandMoreRounded}
                    sx={{ fontSize: 16 }}
                  />
                )
              ) : (
                <ExpandMoreRounded
                  sx={{ color: theme.palette.action.active, fontSize: 16 }}
                />
              )}
            </Box>
          </MenuItem>
        </Tooltip>
      </Box>
      <Menu open={open} onClose={handleClose} anchorEl={anchorEl}>
        {dataNavItems.map((navItem) => {
          const { label, outlinedIcon, path } = navItem;
          const Icon = outlinedIcon;
          const isSelected = location.pathname.startsWith(path);
          return (
            <NavLink
              key={label}
              to={path}
              style={{
                textDecoration: "none",
                color: theme.palette.text.secondary,
              }}>
              <MenuItem
                // onClick={() => handleClickNavItem(navItem)}
                selected={isSelected}>
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <Typography>{label}</Typography>
              </MenuItem>
            </NavLink>
          );
        })}
      </Menu>
    </Fragment>
  );
}

function NavItemsList({
  items,
  expanded,
  recentSessions,
  totalSessionsCount,
  isDarkMode,
}) {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { classes } = useStyles({ expanded });
  const theme = useTheme();

  const handleClickNavItem = (navItem) => {
    if (navItem.disabled) return; // Prevent navigation for disabled items

    const { path } = navItem;
    history.push({
      pathname: path,
    });
    // Only clear session messages when navigating away from AI Search, but keep sessions for sidebar display
    if (
      !location.pathname.startsWith(path) &&
      location.pathname.startsWith(AI_SEARCH_URL)
    ) {
      dispatch(getSessionMessagesSuccess([]));
    }
  };

  return (
    <Box px={1.5}>
      {items.map((navItem, index) => {
        const { label, roundedIcon, outlinedIcon, path, disabled } = navItem;
        const Icon = outlinedIcon;
        const isSelected = location.pathname.startsWith(path);
        const isDisabled = disabled === true;

        // Handle Search highlighting logic
        const hasRecentSessions =
          label === "Search" && recentSessions && recentSessions.length > 0;
        const isOnSearchPath = location.pathname.startsWith(AI_SEARCH_URL);
        const showSelectedBackground =
          isSelected && (!hasRecentSessions || !expanded);
        const showSearchHighlight = label === "Search" && isOnSearchPath;

        const menuItem = (
          <MenuItem
            key={label}
            className={`${
              showSelectedBackground ? classes.activeNavItem : null
            } ${!expanded ? classes.collapsedMenu : null}`}
            sx={{
              padding: 1,
              borderRadius: 1,
              cursor: isDisabled ? "default" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
              "&:hover": {
                backgroundColor: isDisabled ? "transparent" : undefined,
              },
              "&:focus": {
                backgroundColor: isDisabled ? "transparent" : undefined,
              },
            }}
            onClick={() => handleClickNavItem(navItem)}
            disableRipple={isDisabled}
            disableTouchRipple={isDisabled}>
            <ListItemIcon
              classes={{ root: classes.listItemIcon }}
              sx={{ opacity: isDisabled ? 0.6 : 1 }}>
              {(showSearchHighlight || (isSelected && label !== "Search")) &&
              !isDisabled ? (
                !isDarkMode ? (
                  <GradientIconLight Icon={roundedIcon} />
                ) : (
                  <Icon sx={{ color: theme.palette.text.primary }} />
                )
              ) : (
                <Icon />
              )}
            </ListItemIcon>
            <Box
              display="flex"
              flexDirection="column"
              alignItems={expanded ? "flex-start" : "center"}>
              <Typography
                className={classes.label}
                noWrap
                color="textSecondary"
                sx={{ opacity: isDisabled ? 0.6 : 1 }}>
                <span
                  className={
                    (showSearchHighlight || isSelected) && !isDisabled
                      ? classes.labelText
                      : ""
                  }>
                  {label}
                </span>
              </Typography>
              {isDisabled && (
                <Typography
                  className={classes.comingSoonLabel}
                  noWrap
                  sx={{ opacity: 0.4 }}>
                  Coming Soon
                </Typography>
              )}
            </Box>
          </MenuItem>
        );

        return (
          <Fragment key={label}>
            <Tooltip
              title={isDisabled ? `${label} - Coming Soon` : label}
              arrow
              disableHoverListener={expanded}
              placement="right">
              {isDisabled ? (
                menuItem
              ) : (
                <NavLink
                  to={path}
                  style={{
                    textDecoration: "none",
                  }}>
                  {menuItem}
                </NavLink>
              )}
            </Tooltip>
            {/* Show recent sessions under the Search nav item only when expanded */}
            {label === "Search" && expanded && (
              <RecentSessions
                sessions={recentSessions}
                expanded={expanded}
                totalSessionsCount={totalSessionsCount}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
}

export default NavItems;
